import { useEffect, useState } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  Textarea,
  VStack,
  HStack,
  Text,
  Icon,
} from '@chakra-ui/react';
import { FiTarget } from 'react-icons/fi';
import { TargetMarketEntry } from '../../types';

interface TargetMarketModalProps {
  isOpen: boolean;
  onClose: () => void;
  entry: TargetMarketEntry | null;
  isEditing: boolean;
  onSave: (entry: TargetMarketEntry) => void;
}

type SegmentKey = 'demographic' | 'geographic' | 'psychographic';

const SECTIONS: { key: SegmentKey; label: string; placeholder: string }[] = [
  { key: 'demographic', label: 'Demographic', placeholder: 'Ages, income, family status, occupation...' },
  { key: 'geographic', label: 'Geographic', placeholder: 'Regions, cities, proximity, urban / suburban / rural...' },
  { key: 'psychographic', label: 'Psychographic', placeholder: 'Values, motivations, lifestyle, attitudes...' },
];

/**
 * Popup for a single Target Market segment's segmentation details.
 * Edit mode shows textareas; read-only mode renders the saved text (or "Not defined").
 */
export function TargetMarketModal({ isOpen, onClose, entry, isEditing, onSave }: TargetMarketModalProps) {
  const [draft, setDraft] = useState<TargetMarketEntry>(
    entry ?? { name: '', demographic: '', geographic: '', psychographic: '' }
  );

  useEffect(() => {
    if (entry) setDraft(entry);
  }, [entry]);

  if (!entry) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <HStack spacing={2}>
            <Icon as={FiTarget} color="orange.500" />
            <Text>{draft.name || 'Target Market Segment'}</Text>
          </HStack>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4} align="stretch">
            {SECTIONS.map((section) => (
              <FormControl key={section.key}>
                <FormLabel fontSize="sm" fontWeight="semibold">
                  {section.label}
                </FormLabel>
                {isEditing ? (
                  <Textarea
                    rows={4}
                    value={draft[section.key]}
                    placeholder={section.placeholder}
                    onChange={(e) => setDraft((d) => ({ ...d, [section.key]: e.target.value }))}
                  />
                ) : (
                  <Text
                    fontSize="sm"
                    color={draft[section.key] ? 'gray.700' : 'gray.400'}
                    fontStyle={draft[section.key] ? 'normal' : 'italic'}
                    whiteSpace="pre-wrap"
                  >
                    {draft[section.key] || 'Not defined'}
                  </Text>
                )}
              </FormControl>
            ))}
          </VStack>
        </ModalBody>
        {isEditing && (
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>
              Cancel
            </Button>
            <Button colorScheme="blue" onClick={() => onSave(draft)}>
              Save
            </Button>
          </ModalFooter>
        )}
      </ModalContent>
    </Modal>
  );
}
